<template>
  <div class="max-w-6xl mx-auto px-4 sm:px-6 py-12 md:py-16">
    <!-- Main Heading -->
    <div v-reveal class="text-center mb-12">
      <h1 class="font-serif text-5xl md:text-6xl font-bold text-gray-900 leading-none">Gallery</h1>
      <div class="mt-5 w-16 h-1 rounded-full bg-brand-red mx-auto"></div>
    </div>

    <template v-for="(section, si) in sections" :key="section.title">
      <!-- Subheading -->
      <div v-reveal :class="['section-heading mb-6', si > 0 ? 'mt-14' : '']">
        <h2 class="section-title text-2xl">{{ section.title }}</h2>
        <span class="section-subtitle">{{ section.images.length }} photos</span>
      </div>

      <!-- Image Grid: hovering one photo softly dims the others (spotlight) -->
      <div v-reveal.stagger :class="['gallery-grid grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6', section.cols]">
        <button
          v-for="(image, index) in section.images"
          :key="index"
          type="button"
          class="gallery-item"
          :aria-label="`Open ${image.alt}`"
          @click="openViewer(si, index)"
        >
          <img :src="image.src" :alt="image.alt" class="gallery-image" loading="lazy" />
          <span class="gallery-caption">
            <span class="min-w-0 text-left">
              <span class="block text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-cream">{{ section.title }}</span>
              <span class="block truncate text-sm font-medium text-white">Photo {{ index + 1 }}</span>
            </span>
            <span class="gallery-expand"><i class="fa-solid fa-expand"></i></span>
          </span>
        </button>
      </div>
    </template>

    <!-- Full-size viewer -->
    <Teleport to="body">
      <transition name="viewer">
        <div
          v-if="viewer"
          class="fixed inset-0 z-[80] flex flex-col bg-gray-950/90 backdrop-blur-sm"
          @click.self="closeViewer"
          @touchstart.passive="onTouchStart"
          @touchend="onTouchEnd"
        >
          <div class="flex items-center justify-between px-4 py-3 text-white sm:px-6">
            <p class="text-sm">
              <span class="font-semibold">{{ currentSection.title }}</span>
              <span class="ml-2 tabular-nums text-white/60">{{ viewer.index + 1 }} / {{ currentSection.images.length }}</span>
            </p>
            <button class="viewer-btn" aria-label="Close" @click="closeViewer"><i class="fa-solid fa-xmark"></i></button>
          </div>

          <div class="relative flex min-h-0 flex-1 items-center justify-center px-4 pb-6 sm:px-16" @click.self="closeViewer">
            <transition name="swap" mode="out-in">
              <img
                :key="currentImage.src"
                :src="currentImage.src"
                :alt="currentImage.alt"
                class="max-h-full max-w-full rounded-md object-contain shadow-2xl"
              />
            </transition>
            <button class="viewer-btn viewer-nav left-2 sm:left-4" aria-label="Previous photo" @click="step(-1)"><i class="fa-solid fa-chevron-left"></i></button>
            <button class="viewer-btn viewer-nav right-2 sm:right-4" aria-label="Next photo" @click="step(1)"><i class="fa-solid fa-chevron-right"></i></button>
          </div>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script>
import image1 from '../assets/images/Innaguration/new.jpeg';
import image2 from '../assets/images/Innaguration/new2.jpeg';
import image3 from '../assets/images/Innaguration/new3.jpeg';
import release1 from '../assets/images/Magazine_Release/WhatsApp Image 2025-09-28 at 12.51.28 PM.jpeg';
import release2 from '../assets/images/Magazine_Release/WhatsApp Image 2025-09-28 at 12.51.29 PM.jpeg';
import release3 from '../assets/images/Magazine_Release/WhatsApp Image 2025-09-28 at 12.51.29 PM (1).jpeg';
import release4 from '../assets/images/Magazine_Release/WhatsApp Image 2025-09-28 at 12.51.40 PM.jpeg';

export default {
  name: 'GalleryPage',
  data() {
    return {
      sections: [
        {
          title: 'Inauguration',
          cols: 'lg:grid-cols-3',
          images: [
            { src: image1, alt: 'Inauguration Image 1' },
            { src: image2, alt: 'Inauguration Image 2' },
            { src: image3, alt: 'Inauguration Image 3' },
          ],
        },
        {
          title: 'Magazine Release',
          cols: 'lg:grid-cols-4',
          images: [
            { src: release1, alt: 'Magazine Release Image 1' },
            { src: release2, alt: 'Magazine Release Image 2' },
            { src: release3, alt: 'Magazine Release Image 3' },
            { src: release4, alt: 'Magazine Release Image 4' },
          ],
        },
      ],
      viewer: null, // { section, index }
      touchStartX: 0,
    };
  },
  computed: {
    currentSection() {
      return this.viewer ? this.sections[this.viewer.section] : null;
    },
    currentImage() {
      return this.viewer ? this.currentSection.images[this.viewer.index] : null;
    },
  },
  methods: {
    openViewer(section, index) {
      this.viewer = { section, index };
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', this.onKey);
    },
    closeViewer() {
      this.viewer = null;
      document.body.style.overflow = '';
      window.removeEventListener('keydown', this.onKey);
    },
    step(dir) {
      const total = this.currentSection.images.length;
      this.viewer.index = (this.viewer.index + dir + total) % total;
    },
    onKey(e) {
      if (e.key === 'Escape') this.closeViewer();
      else if (e.key === 'ArrowRight') this.step(1);
      else if (e.key === 'ArrowLeft') this.step(-1);
    },
    onTouchStart(e) {
      this.touchStartX = e.changedTouches[0].clientX;
    },
    onTouchEnd(e) {
      const dx = e.changedTouches[0].clientX - this.touchStartX;
      if (Math.abs(dx) > 50) this.step(dx < 0 ? 1 : -1);
    },
  },
  beforeUnmount() {
    if (this.viewer) this.closeViewer();
  },
};
</script>

<style scoped>
.gallery-item {
  position: relative;
  display: block;
  width: 100%;
  overflow: hidden;
  border-radius: 6px;
  background: #e5e7eb;
  cursor: zoom-in;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04), 0 12px 32px -16px rgba(16, 24, 40, 0.18);
  outline: 2px solid transparent;
  outline-offset: 3px;
  transition: transform 0.45s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.45s ease,
    opacity 0.45s ease, filter 0.45s ease, outline-color 0.3s ease;
}

.gallery-image {
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  display: block;
  transition: transform 0.9s cubic-bezier(0.22, 1, 0.36, 1), filter 0.6s ease;
}

/* Caption bar that slides up on hover */
.gallery-caption {
  position: absolute;
  inset: auto 0 0 0;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 2.5rem 1rem 0.9rem;
  background: linear-gradient(to top, rgba(17, 24, 39, 0.78), rgba(17, 24, 39, 0));
  opacity: 0;
  transform: translateY(12px);
  transition: opacity 0.35s ease, transform 0.45s cubic-bezier(0.22, 1, 0.36, 1);
}
.gallery-expand {
  display: flex;
  height: 2rem;
  width: 2rem;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.35);
  color: #fff;
  font-size: 0.75rem;
  backdrop-filter: blur(6px);
}

@media (hover: hover) {
  /* Spotlight: the photo under the pointer stands out, the rest step back */
  .gallery-grid:hover .gallery-item:not(:hover) {
    opacity: 0.55;
    filter: saturate(0.6);
  }
  .gallery-item:hover {
    transform: translateY(-6px);
    outline-color: rgba(216, 64, 64, 0.45);
    box-shadow: 0 2px 4px rgba(16, 24, 40, 0.06), 0 24px 40px -18px rgba(16, 24, 40, 0.35);
  }
  .gallery-item:hover .gallery-image {
    transform: scale(1.03);
    filter: saturate(1.08) contrast(1.03);
  }
  .gallery-item:hover .gallery-caption {
    opacity: 1;
    transform: translateY(0);
  }
}
/* Touch screens: always show the caption, no hover tricks */
@media (hover: none) {
  .gallery-caption {
    opacity: 1;
    transform: none;
  }
}
.gallery-item:focus-visible {
  outline-color: #d84040;
}

/* Viewer */
.viewer-btn {
  display: flex;
  height: 2.75rem;
  width: 2.75rem;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff;
  transition: background 0.2s ease;
}
.viewer-btn:hover {
  background: rgba(255, 255, 255, 0.25);
}
.viewer-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
}

.viewer-enter-active,
.viewer-leave-active {
  transition: opacity 0.25s ease;
}
.viewer-enter-from,
.viewer-leave-to {
  opacity: 0;
}
.swap-enter-active,
.swap-leave-active {
  transition: opacity 0.2s ease, transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
}
.swap-enter-from {
  opacity: 0;
  transform: scale(0.97);
}
.swap-leave-to {
  opacity: 0;
}
</style>
